import type { SplitOptions } from './useSplit'
import { useSplit } from './useSplit'

export type SplitMaturityTag = 'alpha' | 'beta'

export interface SplitEnabledOptions extends SplitOptions {
  /** Treatment value that means "enabled" (default: 'on') */
  enabledTreatment?: string
  /** Treatment to use before SDK is ready (default: 'off') */
  defaultTreatment?: string
}

function readMaturityTag(config: unknown): SplitMaturityTag | null {
  if (config && typeof config === 'object' && 'tag' in config) {
    const { tag } = config
    if (tag === 'alpha' || tag === 'beta') {
      return tag
    }
  }
  return null
}

/**
 * Hook to check if a Split.io feature flag is enabled.
 *
 * Provides a simpler boolean API on top of `useSplit`. Returns `isEnabled: true`
 * when the treatment matches `enabledTreatment` (default: 'on').
 *
 * @template T - Type of the parsed config JSON (defaults to empty object)
 * @param name - The feature flag name
 * @param options - Optional configuration for targeting, treatment values, and key override
 * @returns Object containing isEnabled boolean, ready state, parsed config, and optional maturity tag
 *
 * @example
 * ```tsx
 * const { isEnabled, isReady } = useSplitEnabled('my-feature')
 *
 * if (!isReady) return <Loading />
 * if (isEnabled) return <NewFeature />
 * return <OldFeature />
 * ```
 */
export function useSplitEnabled<T = Record<string, never>>(
  name: string,
  options: SplitEnabledOptions = {},
): {
  isEnabled: boolean
  isReady: boolean
  isReadyFromCache: boolean
  isTimedout: boolean
  config: T
  hasClient: boolean
  /**
   * Maturity tag the split's "on" treatment carries via Split.io dynamic
   * config under the `tag` key, or `null` if unset. Consumers can use
   * this to render a Beta/Alpha badge driven by the Split.io config
   * rather than a hand-written label.
   * @see SplitMaturityTag
   */
  tag: SplitMaturityTag | null
} {
  const {
    enabledTreatment = 'on',
    defaultTreatment = 'off',
    ...splitOptions
  } = options

  const { split, isReady, isReadyFromCache, isTimedout, config, client } =
    useSplit<T>(name, splitOptions)

  const defaultIsEnabled = defaultTreatment === enabledTreatment
  const flagIsEnabled = split.treatment === enabledTreatment

  // No client means the SDK has not settled yet; a timed-out client means it
  // gave up on its first load. Either way we consider it ready and use the
  // cached value if available, otherwise the default. `hasClient` and
  // `isTimedout` tell the two apart for callers that need to.
  if (!client || isTimedout) {
    return {
      isEnabled: isReadyFromCache ? flagIsEnabled : defaultIsEnabled,
      isReady: true,
      isReadyFromCache,
      isTimedout: Boolean(client) && isTimedout,
      // eslint-disable-next-line
      config: {} as T,
      hasClient: Boolean(client),
      tag: isReadyFromCache ? readMaturityTag(config) : null,
    }
  }

  return {
    isEnabled: isReady ? flagIsEnabled : defaultIsEnabled,
    isReady,
    isReadyFromCache,
    isTimedout: false,
    // eslint-disable-next-line
    config: isReady ? config : ({} as T),
    hasClient: true,
    tag: isReady ? readMaturityTag(config) : null,
  }
}

/**
 * Simplified hook that returns only whether a feature flag is enabled.
 *
 * Use this when you only need the boolean value and don't need ready state or config.
 *
 * @param name - The feature flag name
 * @param options - Optional configuration for targeting, treatment values, and key override
 * @returns `true` if the flag is enabled, `false` otherwise
 *
 * @example
 * ```tsx
 * const isEnabled = useIsSplitEnabled('my-feature')
 *
 * return isEnabled ? <NewFeature /> : <OldFeature />
 * ```
 */
export function useIsSplitEnabled<T = Record<string, never>>(
  name: string,
  options?: SplitEnabledOptions,
): boolean {
  const { isEnabled } = useSplitEnabled<T>(name, options)

  return isEnabled
}
